import { ApiTransport } from "../../../network/ApiTransport";
import { MockApiTransport } from "../../../network/MockApiTransport";
import { configHelper } from "../../../config/ConfigHelper";
import { parseNumericCsv } from "../../../network/parseNumericCsv";
import type { IApiTransport } from "../../../definitions/IApiTransport";
import type { LatencyPoint } from "../definitions/LatencyPoint";
import { mockLatencyData } from "../mocks/latencyData.fixture";

/** `?mock=1` swaps in `MockApiTransport` so the dashboard works offline / while the real API is unavailable. */
function isMockEnabled(): boolean {
  return new URLSearchParams(window.location.search).get("mock") === "1";
}

const mockEnabled = isMockEnabled();
if (mockEnabled) {
  console.info("[LatencyDataService] mock=1 — serving latency data from local fixture.");
}

const apiTransport: IApiTransport = mockEnabled
  ? new MockApiTransport(mockLatencyData)
  : new ApiTransport();

function isLatencyPoint(value: unknown): value is LatencyPoint {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === "number" &&
    typeof value[1] === "number"
  );
}

/** One `data[]` entry in the envelope shape below — `{latencybucket, frequency}`. */
interface LatencyBucket {
  latencybucket: number;
  frequency: number;
}

/**
 * The real endpoint's JSON shape as of the `latency.json` migration:
 * `{env, file, label, metric, data: [{latencybucket, frequency}, ...]}`.
 * `label` (e.g. `"Aug 25 - Aug 26"`) is the server's own rolling-window
 * description — `<lmax-chart-range-legend>` displays it as-is rather than
 * recomputing the same range from the client's clock (`formatRollingYearRange`
 * stays as a fallback for data sources that don't supply one, e.g. the CSV
 * fixture used in mock mode).
 */
interface LatencyDataEnvelope {
  label?: unknown;
  data: unknown[];
}

function isLatencyBucket(value: unknown): value is LatencyBucket {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as LatencyBucket).latencybucket === "number" &&
    typeof (value as LatencyBucket).frequency === "number"
  );
}

function isLatencyDataEnvelope(value: unknown): value is LatencyDataEnvelope {
  return typeof value === "object" && value !== null && Array.isArray((value as LatencyDataEnvelope).data);
}

/** Result of a latency fetch: the plotted points, plus the server-supplied period label (if any). */
export interface LatencyDataResult {
  points: LatencyPoint[];
  label: string | null;
}

/**
 * One request per URL per page load, shared by every caller — so a
 * prefetch started at page load (see `prefetchTemplateLatencyCharts`) is
 * picked up by the chart that later asks for the same URL instead of
 * fetching it again. A failed request is evicted so the next caller retries.
 */
const requestCache = new Map<string, Promise<LatencyDataResult>>();

/**
 * Fetches + validates a trade execution latency distribution from an
 * arbitrary URL (e.g. a `src` attribute supplied directly to
 * `<lmax-web-latency-chart>`). Still goes through `apiTransport`, so
 * `?mock=1` swaps in the fixture regardless of which URL was requested.
 */
export function fetchLatencyDataFrom(url: string): Promise<LatencyDataResult> {
  const cached = requestCache.get(url);
  if (cached) return cached;

  const request = requestLatencyData(url);
  requestCache.set(url, request);
  request.catch(() => requestCache.delete(url));
  return request;
}

async function requestLatencyData(url: string): Promise<LatencyDataResult> {
  const raw = await apiTransport.makeAPICall<unknown>(url, apiTransport.getOptions("GET"));

  // `ApiTransport` only decides text vs. JSON — a CSV response comes back as
  // a raw string here, and turning it into `[latency, proportion]` pairs is
  // this service's own job, same as the JSON-shape validation below.
  if (typeof raw === "string") {
    return { points: parseNumericCsv(raw), label: null };
  }

  // `{env, file, label, metric, data: [{latencybucket, frequency}, ...]}` —
  // today's real API response.
  if (isLatencyDataEnvelope(raw)) {
    if (!raw.data.every(isLatencyBucket)) {
      throw new Error(`Unexpected latency data response shape from ${url}`);
    }
    const points: LatencyPoint[] = (raw.data as LatencyBucket[]).map((bucket) => [
      bucket.latencybucket,
      bucket.frequency,
    ]);
    return { points, label: typeof raw.label === "string" ? raw.label : null };
  }

  // Older `[[latency, proportion], ...]` shape — kept for any data source
  // still serving it directly, with no server-supplied label to show.
  if (Array.isArray(raw) && raw.every(isLatencyPoint)) {
    return { points: raw, label: null };
  }

  throw new Error(`Unexpected latency data response shape from ${url}`);
}

/** Fetches the trade execution latency distribution from the configured API. */
export function fetchLatencyData(): Promise<LatencyDataResult> {
  return fetchLatencyDataFrom(configHelper.getEndpointUrl("latencyData"));
}
